import {MdDoorLite} from "../../model/door/MdDoorLite";

// How close (in screen pixels, so the feel is the same at every zoom) an edge has to be
// before it locks onto a snap line.
export const SNAP_PX = 12;

// One snap line the drag locked onto, in leaf-local coords. The drag draws these as guides
// so it is clear what the lite lined up with.
export type SnapGuide = {
    axis: "x" | "y";
    at: number;
    // what the line is: the leaf's own centre, a stile line, or another lite's edge /
    // centre. The guide draws each differently — a door centred lite is worth calling out.
    kind: SnapLineKind;
};

export type SnapLineKind = "centre" | "stile" | "lite";

// the stiles of the leaf, as the lines a lite edge can lock onto.
export type SnapStiles = {side: number; top: number; bottom: number};

export type SnapResult = {
    left: number;
    top: number;
    guides: SnapGuide[];
};

// one candidate line to snap to.
type Line = {at: number; kind: SnapLineKind};

// the lines a dragged lite can lock onto along one axis: the leaf's centre, the two
// stile lines, plus every other lite's near edge, centre and far edge.
function targetLines(others: {near: number; size: number}[], leafSize: number, stileNear: number, stileFar: number): Line[] {
    const out: Line[] = [
        {at: leafSize / 2, kind: "centre"},
        {at: stileNear, kind: "stile"},
        {at: leafSize - stileFar, kind: "stile"},
    ];

    for(const o of others) {
        out.push({at: o.near, kind: "lite"});
        out.push({at: o.near + o.size / 2, kind: "lite"});
        out.push({at: o.near + o.size, kind: "lite"});
    }

    return out;
}

// the x and y target lines of a leaf, for a move or a resize.
function leafLines(props: {leafWidth: number; leafHeight: number; others: MdDoorLite[]; stiles: SnapStiles}): {x: Line[]; y: Line[]} {
    return {
        x: targetLines(
            props.others.map(o => ({near: o.offsetLeft.get(), size: o.width.get()})),
            props.leafWidth, props.stiles.side, props.stiles.side,
        ),
        y: targetLines(
            props.others.map(o => ({near: o.offsetTop.get(), size: o.height.get()})),
            props.leafHeight, props.stiles.top, props.stiles.bottom,
        ),
    };
}

// the best snap for one axis: the smallest move that brings any of the dragged lite's three
// lines (near edge, centre, far edge) onto a target line, or null when nothing is in range.
function snapAxis(axis: "x" | "y", near: number, size: number, lines: Line[], tolerance: number): {delta: number; guide: SnapGuide} | null {
    let best: {delta: number; line: Line} | null = null;

    // the dragged lite's own lines, as offsets from its near edge
    for(const offset of [0, size / 2, size]) {
        for(const line of lines) {
            const delta = line.at - (near + offset);
            if(Math.abs(delta) > tolerance) continue;
            if(best === null || Math.abs(delta) < Math.abs(best.delta)) best = {delta, line};
        }
    }

    if(!best) return null;
    return {delta: best.delta, guide: {axis, at: best.line.at, kind: best.line.kind}};
}

// the best snap for a single dragged edge: the nearest target line within tolerance.
function snapEdge(axis: "x" | "y", at: number, lines: Line[], tolerance: number): {at: number; guide: SnapGuide} | null {
    let best: Line | null = null;
    for(const line of lines) {
        if(Math.abs(line.at - at) > tolerance) continue;
        if(best === null || Math.abs(line.at - at) < Math.abs(best.at - at)) best = line;
    }
    if(!best) return null;
    return {at: best.at, guide: {axis, at: best.at, kind: best.kind}};
}

// Snap a dragged lite's top-left against the leaf centre and its sibling lites. Each axis
// snaps on its own, so a lite can lock to the door centreline horizontally while it lines up
// with another lite's top edge vertically.
//
// `others` is the lites on the same leaf, minus the one being dragged: a lite never snaps to
// itself, and the two leaves of a pair are laid out independently.
export function snapLiteMove(props: {
    left: number;
    top: number;
    width: number;
    height: number;
    leafWidth: number;
    leafHeight: number;
    others: MdDoorLite[];
    stiles: SnapStiles;
    // SNAP_PX converted into leaf-local units for the current zoom.
    tolerance: number;
}): SnapResult {
    const {left, top, width, height, tolerance} = props;
    const lines = leafLines(props);

    const x = snapAxis("x", left, width, lines.x, tolerance);
    const y = snapAxis("y", top, height, lines.y, tolerance);

    const guides: SnapGuide[] = [];
    if(x) guides.push(x.guide);
    if(y) guides.push(y.guide);

    return {
        left: left + (x?.delta ?? 0),
        top: top + (y?.delta ?? 0),
        guides: guides,
    };
}

export type SnapEdgesResult = {
    // the dragged edges after the snap; an edge not being dragged is passed through.
    left: number;
    top: number;
    right: number;
    bottom: number;
    guides: SnapGuide[];
};

// Snap the edges of a lite being resized: each dragged edge (a non-null side) locks onto
// the nearest target line in range — the stile lines, the leaf centre and the sibling
// lites' lines. The held edges are passed through untouched.
export function snapLiteResize(props: {
    left: number;
    top: number;
    right: number;
    bottom: number;
    dragH: "left" | "right" | null;
    dragV: "top" | "bottom" | null;
    leafWidth: number;
    leafHeight: number;
    others: MdDoorLite[];
    stiles: SnapStiles;
    tolerance: number;
}): SnapEdgesResult {
    const lines = leafLines(props);
    const out: SnapEdgesResult = {left: props.left, top: props.top, right: props.right, bottom: props.bottom, guides: []};

    if(props.dragH) {
        const snap = snapEdge("x", out[props.dragH], lines.x, props.tolerance);
        if(snap) {
            out[props.dragH] = snap.at;
            out.guides.push(snap.guide);
        }
    }
    if(props.dragV) {
        const snap = snapEdge("y", out[props.dragV], lines.y, props.tolerance);
        if(snap) {
            out[props.dragV] = snap.at;
            out.guides.push(snap.guide);
        }
    }

    return out;
}
